import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Plus,
  Search,
  RefreshCw,
  Users,
  Monitor,
  Shield,
  DollarSign,
  Eye,
  AlertTriangle,
  CheckCircle,
  X,
  Mail,
  Lock,
  User,
  Wand2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import MSPLayout from '../components/MSPLayout';
import { getMSPClients, createMSPClient } from '../services/api';
import { toast } from 'sonner';

const healthStatusColors = {
  healthy: 'bg-emerald-500/10 text-emerald-500',
  warning: 'bg-amber-500/10 text-amber-500',
  critical: 'bg-red-500/10 text-red-500',
};

const planColors = {
  professional: 'bg-blue-500/10 text-blue-500',
  enterprise: 'bg-purple-500/10 text-purple-500',
};

export default function MSPClients() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    company_name: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    plan: 'professional',
    seats_included: 5,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await getMSPClients();
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.company_name || !newClient.admin_email || !newClient.admin_password || !newClient.admin_name) {
      toast.error('Please fill all required fields');
      return;
    }

    setAddingClient(true);
    try {
      await createMSPClient(newClient);
      toast.success('Client created successfully!');
      setShowAddModal(false);
      setNewClient({
        company_name: '',
        admin_email: '',
        admin_password: '',
        admin_name: '',
        plan: 'professional',
        seats_included: 5,
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setAddingClient(false);
    }
  };

  const filteredClients = clients.filter((client) =>
    client.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalMRR = clients.reduce((sum, c) => sum + (c.billing?.monthly_amount || 0), 0);
  const totalCommission = clients.reduce((sum, c) => sum + (c.billing?.commission || 0), 0);

  if (loading) {
    return (
      <MSPLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </MSPLayout>
    );
  }

  return (
    <MSPLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Client Companies</h1>
            <p className="text-slate-400">Manage your client accounts</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/msp/clients/onboard')} 
              className="border-slate-700 text-slate-300 hover:text-white"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Guided Setup
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Quick Add
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{clients.length}</p>
                  <p className="text-xs text-slate-500">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${totalMRR.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total MRR</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${totalCommission.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">Your Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {clients.filter((c) => c.stats?.health_status !== 'healthy').length}
                  </p>
                  <p className="text-xs text-slate-500">Need Attention</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-800 text-white"
            />
          </div>
          <Button variant="outline" onClick={fetchClients} className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Clients Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0">
            {filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No clients yet</h3>
                <p className="text-slate-400 mb-4">
                  Add your first client company to start managing their security
                </p>
                <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Client
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Users/Devices</TableHead>
                    <TableHead className="text-slate-400">Threats Blocked</TableHead>
                    <TableHead className="text-slate-400">Health</TableHead>
                    <TableHead className="text-slate-400">MRR</TableHead>
                    <TableHead className="text-slate-400">Commission</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => navigate(`/msp/clients/${client.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{client.company_name}</p>
                            <p className="text-xs text-slate-500">
                              {client.seats_used}/{client.seats_included} seats
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={planColors[client.plan] || planColors.professional}>
                          {client.plan}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-500" />
                            {client.stats?.users_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Monitor className="w-4 h-4 text-slate-500" />
                            {client.stats?.devices_count || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-emerald-500" />
                          <span className="text-white">{client.stats?.threats_blocked || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={healthStatusColors[client.stats?.health_status] || healthStatusColors.healthy}>
                          {client.stats?.health_status === 'healthy' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {client.stats?.health_status === 'warning' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {client.stats?.health_status === 'critical' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {client.stats?.health_status || 'healthy'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        ${client.billing?.monthly_amount || 0}
                      </TableCell>
                      <TableCell className="text-emerald-400 font-medium">
                        +${client.billing?.commission?.toFixed(2) || 0}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/msp/clients/${client.id}`);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Client</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new client company under your MSP account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={newClient.company_name}
                  onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                  placeholder="Client Company Name"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Admin Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={newClient.admin_name}
                  onChange={(e) => setNewClient({ ...newClient, admin_name: e.target.value })}
                  placeholder="John Doe"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Admin Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  value={newClient.admin_email}
                  onChange={(e) => setNewClient({ ...newClient, admin_email: e.target.value })}
                  placeholder="admin@client.com"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Admin Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  value={newClient.admin_password}
                  onChange={(e) => setNewClient({ ...newClient, admin_password: e.target.value })}
                  placeholder="Min. 8 characters"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Plan</Label>
                <Select
                  value={newClient.plan}
                  onValueChange={(v) => setNewClient({ ...newClient, plan: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="professional">Professional ($299/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($599/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Seats</Label>
                <Input
                  type="number"
                  min="1"
                  value={newClient.seats_included}
                  onChange={(e) => setNewClient({ ...newClient, seats_included: parseInt(e.target.value) || 5 })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-sm text-blue-400">
                You will earn <strong>30%</strong> commission (${((newClient.plan === 'enterprise' ? 599 : 299) * 0.3).toFixed(2)}/mo) from this client
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddClient}
                disabled={addingClient}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {addingClient ? 'Creating...' : 'Create Client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MSPLayout>
  );
}
